import { useRememberDocument } from '@/contexts/DocumentWorkspace';
import { useIncomingDocument } from '@/contexts/DocumentWorkspace';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import {
  Upload,
  Hash,
  Loader2,
  Download,
  Eye,
} from 'lucide-react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { pdfjsLib } from '@/lib/pdfWorker';
import { readPdfLibBytes, pdfIntakeErrorMessage } from '@/lib/pdfIntake';
import { parsePageRange, formatFileSize } from '@/lib/pdfOperations';
import { UNITS, UNIT_STEP, ptToUnit, unitToPt, clampPt } from '@/lib/units';
import { computePageNumberXY } from '@/lib/pageNumberLayout';
import { useFileDialog } from '@/hooks/useFileDialog';
import CloudSourceHint from '@/components/CloudSourceHint';
import PageCanvas from '@/components/PageCanvas';
import { ProGate } from '@/components/ProBadge';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

const MARGIN_MAX_PT = 288;

const COLORS = [
  { hex: '#000000', label: 'Black' },
  { hex: '#FF0000', label: 'Red' },
  { hex: '#2196F3', label: 'Blue' },
  { hex: '#4CAF50', label: 'Green' },
  { hex: '#FF9800', label: 'Orange' },
  { hex: '#9C27B0', label: 'Purple' },
  { hex: '#607D8B', label: 'Gray' },
];

const POSITIONS = [
  { id: 'top-left', label: 'Top Left' },
  { id: 'top-center', label: 'Top Center' },
  { id: 'top-right', label: 'Top Right' },
  { id: 'bottom-left', label: 'Bottom Left' },
  { id: 'bottom-center', label: 'Bottom Center' },
  { id: 'bottom-right', label: 'Bottom Right' },
];

const FORMATS = [
  { id: 'number', label: '1, 2, 3', fn: (n) => `${n}` },
  { id: 'padded', label: '01, 02, 03', fn: (n, total) => String(n).padStart(String(total).length, '0') },
  { id: 'page-n', label: 'Page 1', fn: (n) => `Page ${n}` },
  { id: 'page-n-of-n', label: 'Page 1 of N', fn: (n, total) => `Page ${n} of ${total}` },
  { id: 'n-of-n', label: '1 of N', fn: (n, total) => `${n} of ${total}` },
  { id: 'dash-n', label: '- 1 -', fn: (n) => `- ${n} -` },
  { id: 'roman', label: 'I, II, III', fn: (n) => toRoman(n) },
];

// `standard` is the pdf-lib font embedded on export; `css` drives the canvas
// preview. Arial maps to the Standard-14 Helvetica because the two are
// metrically compatible (identical advance widths), so the preview measured in
// real Arial matches the exported page-number position exactly — same reasoning
// as resolveFontKey in lib/pdfOperations.js. No font file needed.
const FONTS = [
  { id: 'Arial', label: 'Arial', standard: StandardFonts.Helvetica, css: 'Arial, Helvetica, sans-serif' },
  { id: 'Helvetica', label: 'Helvetica', standard: StandardFonts.Helvetica, css: 'Helvetica, Arial, sans-serif' },
  { id: 'TimesRoman', label: 'Times New Roman', standard: StandardFonts.TimesRoman, css: '"Times New Roman", Times, serif' },
  { id: 'Courier', label: 'Courier', standard: StandardFonts.Courier, css: '"Courier New", Courier, monospace' },
];

const PAGE_MODES = [
  { id: 'all', label: 'All Pages' },
  { id: 'custom', label: 'Custom Range' },
];

function toRoman(num) {
  const vals = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const syms = ['M', 'CM', 'D', 'CD', 'C', 'XC', 'L', 'XL', 'X', 'IX', 'V', 'IV', 'I'];
  let result = '';
  for (let i = 0; i < vals.length; i++) {
    while (num >= vals[i]) {
      result += syms[i];
      num -= vals[i];
    }
  }
  return result;
}

function hexToRgbValues(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return { r, g, b };
}

/**
 * PageNumbers — Add page numbers to every page of a PDF.
 */
export default function PageNumbers() {
  const { t } = useTranslation('tools');
  const navigate = useNavigate();
  const rememberDocument = useRememberDocument();
  const { openFile } = useFileDialog();
  const previewCanvasRef = useRef(null);

  // File state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Settings
  const [position, setPosition] = useState('bottom-center');
  const [format, setFormat] = useState('number');
  const [startNumber, setStartNumber] = useState(1);
  const [fontId, setFontId] = useState('Helvetica');
  const [fontSize, setFontSize] = useState(10);
  const [color, setColor] = useState('#000000');
  const [customColor, setCustomColor] = useState('#000000');
  const [marginUnit, setMarginUnit] = useState('in');
  const [marginH, setMarginH] = useState(36);
  const [marginV, setMarginV] = useState(36);
  // Raw text held per-field so a partial keystroke (e.g. "0.") never gets
  // reformatted/snapped mid-edit — see handleMargin*Change/Blur below.
  const [marginHText, setMarginHText] = useState(() => String(ptToUnit(36, 'in')));
  const [marginVText, setMarginVText] = useState(() => String(ptToUnit(36, 'in')));

  // Page range
  const [pageMode, setPageMode] = useState('all');
  const [customRange, setCustomRange] = useState('');
  const [skipFirst, setSkipFirst] = useState(false);

  // Processing state
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);

  const activeColor = COLORS.find(c => c.hex === color) ? color : customColor;
  const activeFont = FONTS.find(f => f.id === fontId) || FONTS[0];
  const activeFormat = FORMATS.find(f => f.id === format) || FORMATS[0];
  const isCenterPosition = position.endsWith('center');

  // ==================== Margin unit handling ====================

  const selectMarginUnit = useCallback((u) => {
    setMarginUnit(u);
    // Reformat both fields' display text immediately so switching units
    // never leaves stale digits from the previous unit on screen.
    setMarginHText(String(ptToUnit(marginH, u)));
    setMarginVText(String(ptToUnit(marginV, u)));
  }, [marginH, marginV]);

  // Commit to the canonical pt state as soon as the raw text parses to a
  // number — but the input's displayed value is always the raw text itself,
  // so a partial entry like "0." is never reformatted/snapped mid-keystroke.
  const handleMarginHChange = useCallback((raw) => {
    setMarginHText(raw);
    const n = parseFloat(raw);
    if (!Number.isNaN(n)) {
      setMarginH(clampPt(unitToPt(raw, marginUnit), 0, MARGIN_MAX_PT));
    }
  }, [marginUnit]);

  const handleMarginVChange = useCallback((raw) => {
    setMarginVText(raw);
    const n = parseFloat(raw);
    if (!Number.isNaN(n)) {
      setMarginV(clampPt(unitToPt(raw, marginUnit), 0, MARGIN_MAX_PT));
    }
  }, [marginUnit]);

  // On blur, resync the visible text to the canonical (possibly clamped)
  // value so an out-of-range or unparsed entry doesn't linger on screen.
  const handleMarginHBlur = useCallback(() => {
    setMarginHText(String(ptToUnit(marginH, marginUnit)));
  }, [marginH, marginUnit]);

  const handleMarginVBlur = useCallback(() => {
    setMarginVText(String(ptToUnit(marginV, marginUnit)));
  }, [marginV, marginUnit]);

  // ==================== File handling ====================

  const loadFile = useCallback(async (file) => {
    if (!file || file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) return;
    if (pdfDoc) pdfDoc.destroy();

    setError(null);
    setResult(null);
    setFileName(file.name);
    setFileSize(file.size);

    try {
      // Decrypt owner-password PDFs (lossless) / reject truly locked ones
      // before pdf-lib ever sees the bytes — see src/lib/pdfIntake.js.
      const buf = await readPdfLibBytes(file);
      setArrayBuffer(buf);
      const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
      setPdfDoc(doc);
      rememberDocument(file);
      setNumPages(doc.numPages);
    } catch (err) {
      setError(pdfIntakeErrorMessage(err, 'add page numbers'));
    }
  }, [pdfDoc, rememberDocument]);

  useIncomingDocument(loadFile);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    loadFile(e.dataTransfer.files[0]);
  }, [loadFile]);

  const handleBrowse = useCallback(async () => {
    const result = await openFile('.pdf');
    if (result) {
      const file = new File([result.buffer], result.name, { type: 'application/pdf' });
      loadFile(file);
    }
  }, [openFile, loadFile]);

  // ==================== Live preview ====================

  useEffect(() => {
    if (!pdfDoc || !previewCanvasRef.current) return;

    let cancelled = false;

    (async () => {
      // Determine which page to preview: first applicable page
      let previewPageNum = 1;
      if (skipFirst && numPages > 1) previewPageNum = 2;

      const page = await pdfDoc.getPage(previewPageNum);
      const scale = 1.0;
      const viewport = page.getViewport({ scale });
      // True scaling from PDF points to preview canvas pixels — identity at
      // scale 1.0 today, but stays correct if the preview render scale ever
      // changes (e.g. a future DPI-aware preview).
      const previewScale = viewport.width / page.getViewport({ scale: 1 }).width;
      const canvas = previewCanvasRef.current;
      if (!canvas || cancelled) return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      await page.render({ canvasContext: ctx, viewport }).promise;
      if (cancelled) return;

      // Draw page number overlay
      const displayNum = skipFirst ? startNumber + previewPageNum - 2 : startNumber + previewPageNum - 1;
      const totalDisplay = numPages - (skipFirst ? 1 : 0) + startNumber - 1;
      const numberText = activeFormat.fn(displayNum, totalDisplay);

      drawPageNumberPreview(ctx, viewport.width, viewport.height, numberText, previewScale);
    })();

    return () => { cancelled = true; };
  }, [pdfDoc, numPages, position, format, startNumber, fontId, fontSize, activeColor, marginH, marginV, skipFirst, activeFormat]);

  function drawPageNumberPreview(ctx, pw, ph, text, previewScale) {
    ctx.save();
    ctx.font = `${fontSize}px ${activeFont.css}`;
    ctx.fillStyle = activeColor;

    // True scaling from PDF points to preview canvas pixels (see previewScale
    // computation above) — horizontal/vertical margins scale independently.
    const marginHPx = marginH * previewScale;
    const marginVPx = marginV * previewScale;

    let x, y;
    const isTop = position.startsWith('top');
    const isCenter = position.endsWith('center');
    const isRight = position.endsWith('right');

    if (isCenter) {
      ctx.textAlign = 'center';
      x = pw / 2;
    } else if (isRight) {
      ctx.textAlign = 'right';
      x = pw - marginHPx;
    } else {
      ctx.textAlign = 'left';
      x = marginHPx;
    }

    if (isTop) {
      ctx.textBaseline = 'top';
      y = marginVPx;
    } else {
      ctx.textBaseline = 'bottom';
      y = ph - marginVPx;
    }

    ctx.fillText(text, x, y);
    ctx.restore();
  }

  // ==================== Apply page numbers with pdf-lib ====================

  const handleApply = useCallback(async () => {
    if (!arrayBuffer) return;

    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }

    setProcessing(true);
    setProgress(0);
    setError(null);
    setResult(null);

    try {
      const pdfLibDoc = await PDFDocument.load(arrayBuffer);
      const pages = pdfLibDoc.getPages();
      const totalPages = pages.length;

      // Determine target pages
      let targetPageIndices;
      if (pageMode === 'custom' && customRange.trim()) {
        targetPageIndices = parsePageRange(customRange, totalPages).map(p => p - 1);
      } else {
        targetPageIndices = pages.map((_, i) => i);
      }

      // Remove first page if skip is checked
      if (skipFirst) {
        targetPageIndices = targetPageIndices.filter(i => i !== 0);
      }

      // Embed font
      const font = await pdfLibDoc.embedFont(activeFont.standard);

      const totalTarget = targetPageIndices.length;
      const totalDisplay = totalTarget + startNumber - 1;
      const { r, g, b } = hexToRgbValues(activeColor);

      for (let i = 0; i < targetPageIndices.length; i++) {
        const pageIdx = targetPageIndices[i];
        const page = pages[pageIdx];
        const { width: pw, height: ph } = page.getSize();

        const displayNum = startNumber + i;
        const numberText = activeFormat.fn(displayNum, totalDisplay);

        const textWidth = font.widthOfTextAtSize(numberText, fontSize);
        const textHeight = fontSize;

        // Calculate position (pdf-lib: origin is bottom-left)
        const { x, y } = computePageNumberXY({
          pageWidth: pw,
          pageHeight: ph,
          textWidth,
          textHeight,
          position,
          marginH,
          marginV,
        });

        page.drawText(numberText, {
          x,
          y,
          size: fontSize,
          font,
          color: rgb(r, g, b),
        });

        setProgress((i + 1) / targetPageIndices.length);
      }

      const resultBytes = await pdfLibDoc.save();
      const blob = new Blob([resultBytes], { type: 'application/pdf' });
      setResult({ blob, size: resultBytes.length });
      if (usageCapApplies()) incrementUsage();
      setProgress(1);
    } catch (err) {
      setError(`Failed to add page numbers: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  }, [arrayBuffer, position, format, startNumber, fontId, fontSize, activeColor, marginH, marginV,
      pageMode, customRange, skipFirst, activeFormat, activeFont]);

  // ==================== Result actions ====================

  const handleDownload = useCallback(() => {
    if (!result) return;
    const baseName = fileName.replace(/\.pdf$/i, '');
    const url = URL.createObjectURL(result.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}_numbered.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }, [result, fileName]);

  const openInViewer = useCallback(() => {
    if (!result) return;
    const baseName = fileName.replace(/\.pdf$/i, '');
    const file = new File([result.blob], `${baseName}_numbered.pdf`, { type: 'application/pdf' });
    navigate('/viewer', { state: { file } });
  }, [result, fileName, navigate]);

  // ==================== Render ====================

  return (
    <ProGate feature="page-numbers">
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Page Numbers</h1>
            <p className="text-sm text-gray-500">Add page numbers to every page of your PDF</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {/* Drop zone */}
        {!pdfDoc && (
          <>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onClick={handleBrowse}
              className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all mb-6 ${
                isDragging ? 'border-brand-blue bg-brand-light/30 scale-[1.01]' : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
              }`}
            >
              <Upload size={36} className={`mx-auto mb-3 ${isDragging ? 'text-brand-blue' : 'text-gray-400'}`} />
              <p className="text-lg font-medium text-gray-700">Drop a PDF here, or click to browse</p>
              <p className="text-sm text-gray-400 mt-1">Select a PDF to add page numbers</p>
            </div>
            <CloudSourceHint
              onPicked={(r) => loadFile(new File([r.buffer], r.name, { type: 'application/pdf' }))}
              className="mb-6"
            />
            {error && <p role="alert" className="text-red-600 text-sm -mt-3 mb-6">{error}</p>}
          </>
        )}

        {pdfDoc && !result && (
          <>
            {/* File info */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex items-center gap-3">
              <div className="w-8 h-11 rounded border border-gray-200 overflow-hidden shrink-0 bg-white">
                <PageCanvas pdfDoc={pdfDoc} pageNumber={1} scale={0.08} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
                <p className="text-xs text-gray-500">
                  {numPages} page{numPages !== 1 ? 's' : ''} · {formatFileSize(fileSize)}
                </p>
              </div>
              <button onClick={handleBrowse} className="text-sm text-brand-blue hover:underline font-medium">
                Change file
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Settings */}
              <div className="space-y-4">
                {/* Position selector — visual page outline with 6 dots */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <label className="block text-sm font-semibold text-gray-900 mb-3">Position</label>
                  <div className="flex justify-center">
                    <div className="relative w-36 h-48 border-2 border-gray-300 rounded-lg bg-gray-50">
                      {POSITIONS.map((p) => {
                        const isTop = p.id.startsWith('top');
                        const isCenter = p.id.endsWith('center');
                        const isRight = p.id.endsWith('right');
                        const active = position === p.id;

                        const style = {
                          position: 'absolute',
                          top: isTop ? '12px' : undefined,
                          bottom: !isTop ? '12px' : undefined,
                          left: isCenter ? '50%' : isRight ? undefined : '12px',
                          right: isRight ? '12px' : undefined,
                          transform: isCenter ? 'translateX(-50%)' : undefined,
                        };

                        return (
                          <button
                            key={p.id}
                            onClick={() => setPosition(p.id)}
                            title={p.label}
                            style={style}
                            className={`w-5 h-5 rounded-full border-2 transition-all ${
                              active
                                ? 'bg-brand-blue border-brand-blue scale-125 shadow-sm'
                                : 'bg-white border-gray-400 hover:border-brand-blue hover:bg-brand-light/50'
                            }`}
                          />
                        );
                      })}
                      {/* Page icon lines */}
                      <div className="absolute inset-x-5 top-10 space-y-2">
                        <div className="h-1.5 bg-gray-200 rounded-full" />
                        <div className="h-1.5 bg-gray-200 rounded-full w-4/5" />
                        <div className="h-1.5 bg-gray-200 rounded-full" />
                        <div className="h-1.5 bg-gray-200 rounded-full w-3/5" />
                        <div className="h-1.5 bg-gray-200 rounded-full w-4/5" />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    {POSITIONS.find(p => p.id === position)?.label}
                  </p>
                </div>

                {/* Format options */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">Format</h3>

                  {/* Number format */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Number Format</label>
                    <select
                      value={format}
                      onChange={(e) => setFormat(e.target.value)}
                      className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue bg-white"
                    >
                      {FORMATS.map((f) => (
                        <option key={f.id} value={f.id}>{f.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Starting number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Starting Number</label>
                    <input
                      type="number"
                      min={1}
                      value={startNumber}
                      onChange={(e) => setStartNumber(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue"
                    />
                  </div>

                  {/* Font family */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Font</label>
                    <select
                      value={fontId}
                      onChange={(e) => setFontId(e.target.value)}
                      className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue bg-white"
                    >
                      {FONTS.map((f) => (
                        <option key={f.id} value={f.id}>{f.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Font size */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Font Size: {fontSize}pt
                    </label>
                    <input
                      type="range"
                      min={8}
                      max={24}
                      value={fontSize}
                      onChange={(e) => setFontSize(Number(e.target.value))}
                      className="w-full accent-brand-blue"
                    />
                    <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                      <span>8</span><span>24</span>
                    </div>
                  </div>

                  {/* Color */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Color</label>
                    <div className="flex items-center gap-2 flex-wrap">
                      {COLORS.map((c) => (
                        <button
                          key={c.hex}
                          onClick={() => setColor(c.hex)}
                          className={`w-7 h-7 rounded-full border-2 transition-transform ${
                            color === c.hex ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-105'
                          }`}
                          style={{ backgroundColor: c.hex }}
                          title={c.label}
                        />
                      ))}
                      <div className="flex items-center gap-1.5 ml-1">
                        <input
                          type="color"
                          value={customColor}
                          onChange={(e) => { setCustomColor(e.target.value); setColor('custom'); }}
                          className="w-7 h-7 rounded border border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={color === 'custom' ? customColor : color}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (/^#[0-9a-fA-F]{6}$/.test(v)) {
                              setCustomColor(v);
                              setColor('custom');
                            }
                          }}
                          placeholder="#000000"
                          className="w-20 h-7 px-2 text-xs border border-gray-300 rounded focus:outline-none focus:border-brand-blue"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Margin from edge */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t('page_numbers.margin_unit', 'Units')}
                    </label>
                    <div className="flex gap-2 flex-wrap mb-3">
                      {UNITS.map((u) => (
                        <button
                          key={u}
                          type="button"
                          onClick={() => selectMarginUnit(u)}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                            marginUnit === u
                              ? 'border-brand-blue bg-brand-light/50 text-brand-blue font-medium'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {u}
                        </button>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          {t('page_numbers.margin_v', 'Distance from top/bottom edge')}
                        </label>
                        <input
                          type="number"
                          min={0}
                          step={UNIT_STEP[marginUnit]}
                          value={marginVText}
                          onChange={(e) => handleMarginVChange(e.target.value)}
                          onBlur={handleMarginVBlur}
                          className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue"
                        />
                      </div>
                      <div>
                        <label
                          className={`block text-sm font-medium mb-1.5 ${
                            isCenterPosition ? 'text-gray-400' : 'text-gray-700'
                          }`}
                        >
                          {t('page_numbers.margin_h', 'Distance from side edge')}
                        </label>
                        <input
                          type="number"
                          min={0}
                          step={UNIT_STEP[marginUnit]}
                          value={marginHText}
                          onChange={(e) => handleMarginHChange(e.target.value)}
                          onBlur={handleMarginHBlur}
                          disabled={isCenterPosition}
                          className={`w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue disabled:bg-gray-50 disabled:cursor-not-allowed ${
                            isCenterPosition ? 'opacity-50' : ''
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Page range */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">Page Range</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Apply to</label>
                    <div className="flex gap-2 flex-wrap">
                      {PAGE_MODES.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => setPageMode(m.id)}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                            pageMode === m.id
                              ? 'border-brand-blue bg-brand-light/50 text-brand-blue font-medium'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                    {pageMode === 'custom' && (
                      <input
                        type="text"
                        value={customRange}
                        onChange={(e) => setCustomRange(e.target.value)}
                        placeholder="e.g. 1-5, 8, 12"
                        className="mt-2 w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue"
                      />
                    )}
                  </div>

                  {/* Skip first page */}
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={skipFirst}
                      onChange={(e) => setSkipFirst(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue accent-brand-blue"
                    />
                    <span className="text-sm text-gray-700">Skip first page (cover page)</span>
                  </label>
                </div>

                {/* Apply button */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

                  {processing && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Loader2 size={16} className="animate-spin text-brand-blue" />
                        <span className="text-sm text-gray-600">Adding page numbers… {Math.round(progress * 100)}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-brand-blue rounded-full transition-all duration-200" style={{ width: `${progress * 100}%` }} />
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleApply}
                    disabled={processing}
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Hash size={18} />
                    Add Page Numbers
                  </button>
                </div>
              </div>

              {/* Right: Live preview */}
              <div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    Preview {skipFirst && numPages > 1 ? '(Page 2)' : '(Page 1)'}
                  </p>
                  <div className="bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                    <canvas
                      ref={previewCanvasRef}
                      className="max-w-full h-auto"
                      style={{ display: 'block' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Result */}
        {result && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="text-center mb-6">
              <Hash size={40} className="mx-auto mb-3 text-brand-blue" />
              <p className="text-lg font-semibold text-gray-900 mb-1">Page Numbers Added</p>
              <p className="text-sm text-gray-500">
                {formatFileSize(result.size)}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={openInViewer}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors"
              >
                <Eye size={16} />
                Open in Viewer
              </button>
              <button
                onClick={handleDownload}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                <Download size={16} />
                Download
              </button>
            </div>

            <button
              onClick={() => setResult(null)}
              className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 py-2 transition-colors"
            >
              Adjust settings and re-apply
            </button>
          </div>
        )}
      </div>
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
    </ProGate>
  );
}
